import React, { useContext, useState, useEffect, memo, useRef } from "react";
import ReactECharts from "echarts-for-react";
import moment from "moment";
import { get, isEmpty } from "lodash-es";

import NoDataComponent from "features/metadesk/CommunityAnalysis/components/chart/NoDataComponent";
import EmptyIconStatus from "components/EmptyState/EmptyIconStatus";

function mergeArrayToObject(arr) {
  return arr.reduce((acc, curr) => {
    acc.name = curr.name; // 名稱相同，直接設置
    acc.total = (acc.total || 0) + curr.total; // 累加 total
    return acc;
  }, {});
}

const LineChart = ({
  chartData = {
    showDate: "",
    seriesData: [],
  },
  options,
}) => {
  const chartRef = useRef();

  useEffect(() => {
    if (chartRef.current && !isEmpty(chartRef.current)) {
      // 使用 requestAnimationFrame 確保 DOM 渲染完成後執行
      requestAnimationFrame(() => {
        chartRef?.current?.getEchartsInstance()?.resize();
      });
    }
  }, []);
  // 計算每個 series 的總和
  const totals = chartData.seriesData.map((s) => {
    return {
      name: s.name,
      total: s.data.reduce((acc, val) => {
        // 檢查是否為數字
        return typeof val === "number" ? acc + val : acc;
      }, 0),
    };
  });

  const renderLegend = (name) => {
    if (options?.showTotalNumber) {
      const series = totals.filter((s) => s.name === name);
      const mergeSeries = mergeArrayToObject(series);
      if (!isEmpty(series)) {
        return `{name|${mergeSeries.name}}\n{total|${mergeSeries.total}}`;
      } else {
        return name;
      }
    } else {
      const series = totals.find((s) => s.name === name);
      if (series) {
        // 使用 rich 樣式自訂格式
        return `{name|${series.name}}`;
      } else {
        return name;
      }
    }
  };

  const getOptions = (newSeries) => {
    return {
      legend: {
        show: true, // 確保圖例顯示
        orient: "horizontal", // 將圖例設置為水平排列
        left: "left", // 水平居中
        itemWidth: 12, // 減小圖例標記的寬度
        itemHeight: 8, // 減小圖例標記的高度
        icon: "roundRect",
        type: chartData?.seriesData?.length > 3 ? "scroll" : "plain",
        itemStyle: {
          borderColor: "transparent", // 移除外框
          borderWidth: 0, // 設置外框寬度為 0
        },
        formatter: function (name) {
          return renderLegend(name);
        },
        textStyle: {
          lineHeight: options?.showTotalNumber ? 18 : 12,
          rich: {
            name: {
              fontSize: 12,
              color: "#333",
              verticalAlign: "top", // 讓文字與方塊對齊
            },
            total: {
              fontSize: 18,
              fontWeight: 700,
              color: "#333",
            },
          },
        },
        top: "0",
      },
      grid: {
        bottom: "0%", // 控制圖表下方距離
        left: "2%", // 控制圖表左側距離
        right: "5%", // 控制圖表右側距離
        containLabel: true, // 確保標籤不會被裁切
      },
      tooltip: {
        trigger: "axis",
        formatter: function (params) {
          const date = moment(params[0].name).format("DD MMM YYYY"); // 取得X軸的日期

          let result = `<div style="margin-bottom:15px">${date}</div>`;

          if (options?.chartTitle) {
            result += `<div style="color:"#555;margin-bottom:5px;font-weight:900">${options?.chartTitle}</div>`;
          }
          params.forEach((param) => {
            result += `<div style="min-width:200px; display: flex; align-items: center;">${
              param.marker
            } ${param.seriesName}: <span style="margin-left:auto">${
              typeof param.data === "number"
                ? parseFloat(Number(param.data).toFixed(2))
                : param.data
            }</span> </div>`;
          });
          return result;
        },
      },

      xAxis: {
        type: "category",
        boundaryGap: false,
        data: chartData?.showDate, // 保留完整日期資料
        axisLabel: {
          formatter: function (value) {
            return moment(value).format("DD MMM"); // 隱藏年份
          },
        },
      },
      yAxis: {
        type: "value",
      },
      series: chartData?.seriesData.map((item) => ({
        ...item,
      })),
    };
  };

  const newOption = Object.assign(getOptions(), options);

  return isEmpty(chartData.seriesData) ? (
    <EmptyIconStatus icon="chart" />
  ) : (
    <ReactECharts
      option={newOption}
      ref={chartRef}
      autoResize={true}
      style={{ height: "100%" }}
      notMerge={true}
    />
  );
};

export default LineChart;
